import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  PipelineDecision,
  PipelineDecisionsParams,
  getPipelineDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface PipelineSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const PipelineSection: React.FC<PipelineSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [recIdFilter, setRecIdFilter] = React.useState('');

  const params = useMemo<PipelineDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    recommendation_id: recIdFilter || undefined,
  }), [dateFrom, dateTo, symbol, recIdFilter]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } = useFlowData<PipelineDecision, PipelineDecisionsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'pipeline'],
    fetchFn: (p) => getPipelineDecisions(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  const getScoreClass = (score: number | null) => {
    if (score === null) return '';
    if (score >= 0.8) return styles.scoreHigh;
    if (score >= 0.6) return styles.scoreMedium;
    return styles.scoreLow;
  };

  return (
    <DataSection
      title="Pipeline Filters"
      tabNumber={3}
      description="Technical, VWAP, session, market structure, and regime filter decisions. Each pair is scored across multiple filter categories."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <input
          type="text"
          placeholder="Filter by Recommendation ID..."
          value={recIdFilter}
          onChange={(e) => { setRecIdFilter(e.target.value); setPage(1); }}
          className={styles.filterInput}
        />
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Rec ID</th>
            <th>Filter</th>
            <th>Result</th>
            <th>Value</th>
            <th>Score</th>
            <th>Direction</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td className={styles.monoSmall}>{d.recommendation_id.slice(0, 12)}...</td>
              <td>{d.filter_name}{d.filter_sub_name ? ` · ${d.filter_sub_name}` : ''}</td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
              <td className={styles.mono}>
                {d.actual_value !== null ? d.actual_value.toFixed(6) : '-'}
              </td>
              <td>
                <span className={`${styles.scoreBadge} ${getScoreClass(d.overall_score)}`}>
                  {d.overall_score !== null ? d.overall_score.toFixed(3) : '-'}
                </span>
              </td>
              <td>{d.direction || '-'}</td>
              <td className={styles.detailCell}>{d.detail || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default PipelineSection;
